import { FC, Key, useMemo } from 'react';
import { Checkbox, Divider, Flex } from 'antd';
import { TbCalendar, TbFile, TbFileDescription, TbPaperclip } from 'react-icons/tb';

import {
    createFilterableColumn,
    FilterableColumn,
    ListFilterRule,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import { WorkspaceResourceFilterControls } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/WorkspaceResourceFiltersRow';
import { WorkspaceFolderGlyph, WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';
import { ListViewColumnDataType } from '@/types';

interface AttachmentTypeOption {
    label: string;
    value: string;
}

interface AttachmentFiltersRowProps {
    filters: ListFilterRule[];
    onChange: (filters: ListFilterRule[]) => void;
    onSelectedKeysChange: (selectedKeys: ReadonlySet<Key>) => void;
    selectedKeys: ReadonlySet<Key>;
    typeOptions: AttachmentTypeOption[];
    visibleAttachmentIds: string[];
}

const AttachmentFiltersRow: FC<AttachmentFiltersRowProps> = ({
    filters,
    onChange,
    onSelectedKeysChange,
    selectedKeys,
    typeOptions,
    visibleAttachmentIds,
}) => {
    const columns = useMemo<FilterableColumn[]>(
        () => [
            createFilterableColumn({
                dataType: ListViewColumnDataType.SELECT,
                filterOperators: ['equals', 'notEquals'],
                filterOptions: [
                    { icon: <TbFile />, label: 'Files', value: 'file' },
                    { icon: <WorkspaceResourceGlyph resource='folders' />, label: 'Folders', value: 'folder' },
                ],
                icon: <TbPaperclip size='0.875rem' />,
                key: 'kind',
                label: 'Kind',
                order: 0,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.SELECT,
                filterOperators: ['equals', 'notEquals'],
                filterOptions: typeOptions.map((option) => ({
                    icon: <TbFileDescription />,
                    ...option,
                })),
                icon: <TbFileDescription size='0.875rem' />,
                key: 'mimeType',
                label: 'File Type',
                order: 1,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.LONG_TEXT,
                icon: <TbFile size='0.875rem' />,
                key: 'name',
                label: 'Name',
                order: 2,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.LONG_TEXT,
                icon: <WorkspaceFolderGlyph size='0.875rem' variant='open' />,
                key: 'relativePath',
                label: 'Folder Path',
                order: 3,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.DATE,
                icon: <TbCalendar size='0.875rem' />,
                key: 'createdTime',
                label: 'Added Date',
                order: 4,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.NUMBER,
                key: 'size',
                label: 'Size',
                order: 5,
            }),
        ],
        [typeOptions],
    );

    return (
        <WorkspaceResourceFilterControls
            columns={columns}
            docsLabel='Attachment filters'
            emptyColumnsDescription='Attachment properties become filterable after files or folders are available.'
            emptyHint='Filter Attachments by kind, file type, name, folder path, date, or size.'
            filterableResourceLabel='Attachments'
            filters={filters}
            horizontalPadding='1rem'
            leading={
                <Flex align='center'>
                    <Checkbox
                        data-compact-list-selection-control='true'
                        aria-label='Select all visible attachments'
                        checked={
                            visibleAttachmentIds.length > 0 &&
                            visibleAttachmentIds.every((attachmentId) => selectedKeys.has(attachmentId))
                        }
                        disabled={visibleAttachmentIds.length === 0}
                        indeterminate={
                            visibleAttachmentIds.some((attachmentId) => selectedKeys.has(attachmentId)) &&
                            !visibleAttachmentIds.every((attachmentId) => selectedKeys.has(attachmentId))
                        }
                        onChange={(event) => {
                            const nextSelectedKeys = new Set(selectedKeys);

                            visibleAttachmentIds.forEach((attachmentId) => {
                                if (event.target.checked) {
                                    nextSelectedKeys.add(attachmentId);
                                } else {
                                    nextSelectedKeys.delete(attachmentId);
                                }
                            });
                            onSelectedKeysChange(nextSelectedKeys);
                        }}
                    />
                    <Divider type='vertical' style={{ margin: '0 0.75rem' }} />
                </Flex>
            }
            onChange={(nextFilters) => onChange(nextFilters ?? [])}
            resourceLabel='Attachments'
        />
    );
};

export default AttachmentFiltersRow;
